import { LoginCredentials, LoginResponse, User, RegisterData } from '../types/auth'
import {
  Product,
  Category,
  DashboardStats,
  Invoice,
  InvoiceStats,
  InvoiceFilters,
  InvoiceListResponse,
  InvoiceFormData,
  PaymentData,
  Role,
  RoleFormData,
  PermissionsByModule,
  Expense,
  ExpenseCategory,
  ExpenseFormData,
  ExpenseFilters,
  ExpenseStats,
  Supplier,
  SupplierFormData,
  SupplierFilters,
  SupplierStats,
  Purchase,
  PurchaseFormData,
  PurchaseFilters,
  PurchaseStats
} from '../types/api'

// Get API URL from environment variable, fallback to localhost for development
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api'

// Import auth store dynamically to avoid circular dependencies
let authStore: any = null
const getAuthStore = async () => {
  if (!authStore) {
    const { useAuthStore } = await import('../stores/authStore')
    authStore = useAuthStore
  }
  return authStore
}

// Helper function to transform product numeric fields from string to number
const transformProduct = (product: any) => ({
  ...product,
  price: typeof product.price === 'string' ? parseFloat(product.price) || 0 : product.price || 0,
  mrp: typeof product.mrp === 'string' ? parseFloat(product.mrp) || null : product.mrp,
  cost_price: typeof product.cost_price === 'string' ? parseFloat(product.cost_price) || null : product.cost_price,
  gst_rate: typeof product.gst_rate === 'string' ? parseFloat(product.gst_rate) || 0 : product.gst_rate || 0,
  stock_quantity: typeof product.stock_quantity === 'string' ? parseFloat(product.stock_quantity) || 0 : product.stock_quantity || 0,
  min_stock_level: typeof product.min_stock_level === 'string' ? parseFloat(product.min_stock_level) || 0 : product.min_stock_level || 0,
  max_stock_level: typeof product.max_stock_level === 'string' ? parseFloat(product.max_stock_level) || 0 : product.max_stock_level || 0
})

class ApiClient {
  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token')
    return !!token
  }

  // Check if endpoint requires authentication
  private requiresAuth(endpoint: string): boolean {
    // Auth endpoints don't require authentication
    if (endpoint.includes('/auth/login') || endpoint.includes('/auth/register') || endpoint.includes('/auth/forgot-password') || endpoint.includes('/auth/verify-otp') || endpoint.includes('/auth/reset-password')) {
      return false
    }
    // Auditor invitation endpoints don't require authentication
    if (endpoint.includes('/auditors/invitation-details') || endpoint.includes('/auditors/accept-invitation')) {
      return false
    }
    // All other endpoints require authentication
    return true
  }

  // Clear all auth data and redirect to login
  private async handleAuthFailure(): Promise<void> {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('refresh_token')
    
    try {
      // Update auth store state to trigger re-render
      const store = await getAuthStore()
      store.getState().logout()
    } catch (error) {
      console.warn('Could not access auth store:', error)
    }
    
    console.log('Authentication failed, user logged out')
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token')

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // For auditors, add the selected merchant ID header
    try {
      const auditorState = localStorage.getItem('auditor-storage')
      if (auditorState) {
        const parsed = JSON.parse(auditorState)
        if (parsed.state?.isAuditor && parsed.state?.currentBusiness?.merchant_id) {
          const merchantId = parsed.state.currentBusiness.merchant_id
          headers['X-Merchant-Id'] = merchantId
          console.log('[API] Adding X-Merchant-Id header:', merchantId, 'for business:', parsed.state.currentBusiness.name)
        } else {
          console.log('[API] No merchant_id found in auditor state:', parsed.state?.currentBusiness)
        }
      }
    } catch (error) {
      console.error('[API] Error parsing auditor state:', error)
    }

    return headers
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    // Check if authentication is required and available
    if (this.requiresAuth(endpoint) && !this.isAuthenticated()) {
      console.log('No auth token found for protected endpoint:', endpoint)
      await this.handleAuthFailure()
      throw new Error('Authentication required. Please login again.')
    }
    
    const makeRequest = async (): Promise<Response> => {
      const headers = {
        ...this.getAuthHeaders(),
        ...options.headers,
      }
      
      // Debug logging for auth headers
      if (this.requiresAuth(endpoint)) {
        console.log('Making authenticated request to:', endpoint)
        console.log('Auth header present:', !!headers.Authorization)
      }
      
      return fetch(url, {
        ...options,
        headers,
      })
    }

    let response = await makeRequest()

    // Handle 401 errors by attempting token refresh
    if (response.status === 401 && !endpoint.includes('/auth/')) {
      const refreshToken = localStorage.getItem('refresh_token')
      
      if (refreshToken) {
        try {
          // Try to refresh the token directly to avoid circular dependency
          const refreshUrl = `${API_BASE_URL}/auth/refresh`
          const refreshResponse = await fetch(refreshUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
          })
          
          if (!refreshResponse.ok) {
            throw new Error('Refresh failed')
          }
          
          const refreshData = await refreshResponse.json()

          // Unwrap data field if present
          const tokenData = refreshData.data || refreshData

          // Update stored tokens
          localStorage.setItem('auth_token', tokenData.token)
          if (tokenData.refresh_token) {
            localStorage.setItem('refresh_token', tokenData.refresh_token)
          }
          
          // Retry the original request with new token
          response = await makeRequest()
        } catch (refreshError) {
          // Refresh failed, redirect to login
          await this.handleAuthFailure()
          throw new Error('Session expired. Please login again.')
        }
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      // Handle authentication errors
      if (response.status === 401 || errorData.error === 'MISSING_AUTH_HEADER' || errorData.error === 'INVALID_TOKEN') {
        await this.handleAuthFailure()
        throw new Error('Authentication required. Please login again.')
      }

      // Extract error message from various formats
      // CodeIgniter format: { messages: { error: "..." } }
      // Standard format: { message: "..." }
      // Legacy format: { error: "..." }
      const errorMessage =
        errorData.message ||
        errorData.messages?.error ||
        (typeof errorData.error === 'string' ? errorData.error : null) ||
        `HTTP ${response.status}`

      throw new Error(errorMessage)
    }

    return response.json()
  }

  // Health check
  async healthCheck(): Promise<any> {
    return this.request('/health')
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response: any = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
    // Unwrap the data field if present
    return response.data || response
  }

  async register(data: RegisterData): Promise<LoginResponse> {
    const response: any = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    // Unwrap the data field if present
    return response.data || response
  }

  async getCurrentUser(): Promise<{ user: User }> {
    const response: any = await this.request('/auth/me')
    // Unwrap the data field if present
    return response.data || response
  }

  async updateProfile(data: { id: string; full_name: string; email: string; phone: string; business_name: string }): Promise<{ user: User }> {
    return this.request('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<{ message: string }> {
    return this.request('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getSettings(): Promise<any> {
    return this.request('/settings')
  }

  async saveSettings(category: string, data: any): Promise<any> {
    return this.request(`/settings/${category}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateMerchant(data: any): Promise<any> {
    return this.request('/merchant', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async refreshToken(refreshToken: string): Promise<{ token: string; refresh_token?: string; user?: User }> {
    const response: any = await this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    // Unwrap the data field if present
    return response.data || response
  }

  // Products
  async getProducts(options?: {
    limit?: number
    offset?: number
    category_id?: string
    search?: string
    is_active?: boolean
    sort?: string
  }): Promise<{ products: Product[]; pagination: { total: number; limit: number; offset: number } }> {
    let endpoint = '/products'
    
    if (options) {
      const params = new URLSearchParams()
      if (options.limit) params.append('limit', options.limit.toString())
      if (options.offset) params.append('offset', options.offset.toString())
      if (options.category_id) params.append('category_id', options.category_id)
      if (options.search) params.append('search', options.search)
      if (options.is_active !== undefined) params.append('is_active', options.is_active.toString())
      if (options.sort) params.append('sort', options.sort)
      
      endpoint += `?${params.toString()}`
    }
    
    const response:any = await this.request(endpoint)
    
    // Handle the nested data structure from API response
    const productsData = response.data?.products || response.products || []
    const paginationData = response.data?.pagination || response.pagination || { total: productsData.length, limit: 50, offset: 0 }
    
    // Transform numeric string fields to actual numbers
    const transformedProducts = productsData.map(transformProduct)
    
    return { 
      products: transformedProducts,
      pagination: paginationData
    }
  }

  async getProduct(id: string): Promise<{ product: Product }> {
    const response:any = await this.request(`/products/${id}`)
    
    // Handle the nested data structure from API response
    const productData = response.data?.product || response.product
    
    // Transform numeric string fields to actual numbers
    const transformedProduct = transformProduct(productData)
    
    return { product: transformedProduct }
  }

  async createProduct(productData: any): Promise<{ product: Product }> {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    })
  }

  async updateProduct(id: string, productData: any): Promise<{ product: Product }> {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    })
  }

  async deleteProduct(id: string): Promise<{ success: boolean }> {
    return this.request(`/products/${id}`, {
      method: 'DELETE',
    })
  }

  async searchProductByCode(code: string, includeInactive: boolean = false): Promise<{ product: Product | null }> {
    try {
      const url = `/products/search/${encodeURIComponent(code)}${includeInactive ? '?includeInactive=true' : ''}`
      const response: any = await this.request(url)
      const productData = response.data?.product || response.product
      return { product: productData ? transformProduct(productData) : null }
    } catch (error) {
      // Return null if not found instead of throwing
      return { product: null }
    }
  }

  // Categories
  async getCategories(): Promise<{ categories: Category[] }> {
    return this.request('/categories')
  }

  async getCategory(id: string): Promise<{ category: Category }> {
    return this.request(`/categories/${id}`)
  }

  async createCategory(categoryData: any): Promise<{ category: Category }> {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    })
  }

  async updateCategory(id: string, categoryData: any): Promise<{ category: Category }> {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    })
  }

  async deleteCategory(id: string): Promise<{ success: boolean }> {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    })
  }

  // Units
  async getUnits(): Promise<{ units: any[] }> {
    return this.request('/units')
  }

  async getUnit(id: string): Promise<{ unit: any }> {
    return this.request(`/units/${id}`)
  }

  async createUnit(unitData: any): Promise<{ unit: any }> {
    return this.request('/units', {
      method: 'POST',
      body: JSON.stringify(unitData),
    })
  }

  async updateUnit(id: string, unitData: any): Promise<{ unit: any }> {
    return this.request(`/units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(unitData),
    })
  }

  async deleteUnit(id: string): Promise<{ success: boolean }> {
    return this.request(`/units/${id}`, {
      method: 'DELETE',
    })
  }

  // Stock Management
  async adjustStock(adjustmentData: any): Promise<{ success: boolean, movement: any }> {
    return this.request('/stock/adjust', {
      method: 'POST',
      body: JSON.stringify(adjustmentData),
    })
  }

  async getStockMovements(productId?: string): Promise<{ movements: any[] }> {
    const endpoint = productId ? `/stock/movements?product_id=${productId}` : '/stock/movements'
    return this.request(endpoint)
  }

  async getLowStockProducts(): Promise<{ products: any[] }> {
    return this.request('/stock/low')
  }


  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    const response: any = await this.request('/dashboard')
    return response.data || response
  }

  // Sales/Transactions Management
  async createSale(saleData: any): Promise<{ success: boolean, sale: any, bill: any }> {
    return this.request('/sales', {
      method: 'POST',
      body: JSON.stringify(saleData),
    })
  }

  async getSales(filters?: any): Promise<{ sales: any[] }> {
    const queryParams = filters ? `?${new URLSearchParams(filters)}` : ''
    return this.request(`/sales${queryParams}`)
  }

  async getSale(saleId: string): Promise<{ sale: any }> {
    return this.request(`/sales/${saleId}`)
  }

  async getRecentSales(): Promise<{ sales: any[] }> {
    return this.request('/sales/recent')
  }

  async getSalesReport(startDate: string, endDate: string): Promise<any> {
    return this.request(`/sales/report?start_date=${startDate}&end_date=${endDate}`)
  }

  // Customer Management
  async getCustomers(): Promise<{ customers: any[] }> {
    return this.request('/customers')
  }

  async createCustomer(customerData: any): Promise<{ success: boolean, customer: any }> {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    })
  }

  async updateCustomer(customerId: string, customerData: any): Promise<{ success: boolean, customer: any }> {
    return this.request(`/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    })
  }

  async deleteCustomer(customerId: string): Promise<{ success: boolean }> {
    return this.request(`/customers/${customerId}`, {
      method: 'DELETE',
    })
  }

  // Invoice Management
  async getInvoices(filters?: InvoiceFilters): Promise<InvoiceListResponse> {
    let endpoint = '/invoices'

    if (filters) {
      const params = new URLSearchParams()
      if (filters.payment_status) params.append('payment_status', filters.payment_status)
      if (filters.customer_id) params.append('customer_id', filters.customer_id)
      if (filters.date_from) params.append('date_from', filters.date_from)
      if (filters.date_to) params.append('date_to', filters.date_to)
      if (filters.search) params.append('search', filters.search)
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())

      endpoint += `?${params.toString()}`
    }

    const response: any = await this.request(endpoint)
    return response.data || response
  }

  async getInvoice(id: string): Promise<{ data: Invoice }> {
    const response: any = await this.request(`/invoices/${id}`)
    return response.data || response
  }

  async createInvoice(invoiceData: InvoiceFormData): Promise<{ data: Invoice }> {
    const response: any = await this.request('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    })
    return response.data || response
  }

  async updateInvoice(id: string, invoiceData: Partial<InvoiceFormData>): Promise<{ data: Invoice }> {
    const response: any = await this.request(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    })
    return response.data || response
  }

  async deleteInvoice(id: string): Promise<{ success: boolean }> {
    const response: any = await this.request(`/invoices/${id}`, {
      method: 'DELETE',
    })
    return response.data || response
  }

  async getInvoiceStats(): Promise<InvoiceStats> {
    const response: any = await this.request('/invoices/stats')
    return response.data || response
  }

  async getOverdueInvoices(limit?: number): Promise<{ invoices: Invoice[]; count: number }> {
    const endpoint = limit ? `/invoices/overdue?limit=${limit}` : '/invoices/overdue'
    const response: any = await this.request(endpoint)
    return response.data || response
  }

  async recordInvoicePayment(invoiceId: string, paymentData: PaymentData): Promise<{ data: Invoice }> {
    const response: any = await this.request(`/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    })
    return response.data || response
  }

  // Password Reset with OTP
  async forgotPassword(data: { email: string }): Promise<any> {
    const response: any = await this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data || response
  }

  async verifyOTP(data: { email: string; otp_code: string }): Promise<any> {
    const response: any = await this.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data || response
  }

  async resetPassword(data: { email: string; password: string; password_confirmation: string }): Promise<any> {
    const response: any = await this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return response.data || response
  }

  // Role Management
  async getRoles(): Promise<{ success: boolean; roles: Role[] }> {
    return this.request('/roles')
  }

  async getRole(roleId: string): Promise<{ success: boolean; role: Role }> {
    return this.request(`/roles/${roleId}`)
  }

  async createRole(roleData: RoleFormData): Promise<{ success: boolean; role: Role; message: string }> {
    return this.request('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    })
  }

  async updateRole(roleId: string, roleData: Partial<RoleFormData>): Promise<{ success: boolean; role: Role; message: string }> {
    return this.request(`/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    })
  }

  async deleteRole(roleId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/roles/${roleId}`, {
      method: 'DELETE',
    })
  }

  async getPermissions(): Promise<{ success: boolean; permissions: PermissionsByModule }> {
    return this.request('/roles/permissions')
  }

  async initializeDefaultRoles(): Promise<{ success: boolean; roles: Role[]; message: string }> {
    return this.request('/roles/initialize', {
      method: 'POST',
    })
  }

  // User Management
  async getUsers(): Promise<{ success: boolean; users: any[] }> {
    return this.request('/users')
  }

  async getUser(userId: string): Promise<{ success: boolean; user: any }> {
    return this.request(`/users/${userId}`)
  }

  async createUser(userData: any): Promise<{ success: boolean; user: any; message: string }> {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async updateUser(userId: string, userData: any): Promise<{ success: boolean; user: any; message: string }> {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    })
  }

  async deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/users/${userId}`, {
      method: 'DELETE',
    })
  }

  // Global Search
  async globalSearch(query: string, limit?: number): Promise<{ products: any[]; orders: any[]; customers: any[] }> {
    const params = new URLSearchParams()
    params.append('q', query)
    if (limit) params.append('limit', limit.toString())

    const response: any = await this.request(`/search?${params.toString()}`)
    return response.data || response
  }

  // Expense Categories
  async getExpenseCategories(withExpenseCount: boolean = false): Promise<{ categories: ExpenseCategory[] }> {
    const params = withExpenseCount ? '?with_expense_count=true' : ''
    const response: any = await this.request(`/expense-categories${params}`)
    return response.data || response
  }

  async getExpenseCategory(id: string): Promise<{ category: ExpenseCategory }> {
    const response: any = await this.request(`/expense-categories/${id}`)
    return response.data || response
  }

  async createExpenseCategory(categoryData: Partial<ExpenseCategory>): Promise<{ category: ExpenseCategory; message: string }> {
    return this.request('/expense-categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    })
  }

  async updateExpenseCategory(id: string, categoryData: Partial<ExpenseCategory>): Promise<{ category: ExpenseCategory; message: string }> {
    return this.request(`/expense-categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    })
  }

  async deleteExpenseCategory(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/expense-categories/${id}`, {
      method: 'DELETE',
    })
  }

  // Expenses
  async getExpenses(filters?: ExpenseFilters): Promise<{ expenses: Expense[]; pagination: any }> {
    let endpoint = '/expenses'

    if (filters) {
      const params = new URLSearchParams()
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())
      if (filters.date_from) params.append('date_from', filters.date_from)
      if (filters.date_to) params.append('date_to', filters.date_to)
      if (filters.category_id) params.append('category_id', filters.category_id)
      if (filters.payment_method) params.append('payment_method', filters.payment_method)
      if (filters.is_recurring !== undefined) params.append('is_recurring', filters.is_recurring.toString())
      if (filters.search) params.append('search', filters.search)
      if (filters.order_by) params.append('order_by', filters.order_by)
      if (filters.order_dir) params.append('order_dir', filters.order_dir)
      endpoint += `?${params.toString()}`
    }

    const response: any = await this.request(endpoint)
    return response.data || response
  }

  async getExpense(id: string): Promise<{ expense: Expense }> {
    const response: any = await this.request(`/expenses/${id}`)
    return response.data || response
  }

  async createExpense(expenseData: ExpenseFormData): Promise<{ expense: Expense; message: string }> {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData),
    })
  }

  async updateExpense(id: string, expenseData: Partial<ExpenseFormData>): Promise<{ expense: Expense; message: string }> {
    return this.request(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData),
    })
  }

  async deleteExpense(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/expenses/${id}`, {
      method: 'DELETE',
    })
  }

  async getExpenseStats(startDate?: string, endDate?: string, categoryId?: string): Promise<ExpenseStats> {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    if (categoryId) params.append('category_id', categoryId)

    const endpoint = params.toString() ? `/expenses/stats?${params.toString()}` : '/expenses/stats'
    const response: any = await this.request(endpoint)
    return response.data || response
  }

  // Suppliers
  async getSuppliers(filters?: SupplierFilters): Promise<{ suppliers: Supplier[]; pagination: any }> {
    let endpoint = '/suppliers'

    if (filters) {
      const params = new URLSearchParams()
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
      if (filters.search) params.append('search', filters.search)
      if (filters.sort) params.append('sort', filters.sort)
      endpoint += `?${params.toString()}`
    }

    const response: any = await this.request(endpoint)
    return response.data || response
  }

  async getSupplier(id: string): Promise<{ supplier: Supplier }> {
    const response: any = await this.request(`/suppliers/${id}`)
    return response.data || response
  }

  async createSupplier(supplierData: SupplierFormData): Promise<{ supplier: Supplier; message: string }> {
    return this.request('/suppliers', {
      method: 'POST',
      body: JSON.stringify(supplierData),
    })
  }

  async updateSupplier(id: string, supplierData: Partial<SupplierFormData>): Promise<{ supplier: Supplier; message: string }> {
    return this.request(`/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData),
    })
  }

  async deleteSupplier(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/suppliers/${id}`, {
      method: 'DELETE',
    })
  }

  async getSupplierStats(): Promise<SupplierStats> {
    const response: any = await this.request('/suppliers/stats')
    return response.data?.stats || response.stats || response
  }

  async searchSuppliers(query: string, limit?: number): Promise<{ suppliers: Supplier[] }> {
    const params = new URLSearchParams()
    params.append('q', query)
    if (limit) params.append('limit', limit.toString())

    const response: any = await this.request(`/suppliers/search?${params.toString()}`)
    return response.data || response
  }

  // Purchases
  async getPurchases(filters?: PurchaseFilters): Promise<{ purchases: Purchase[]; pagination: any }> {
    let endpoint = '/purchases'
    if (filters) {
      const params = new URLSearchParams()
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())
      if (filters.supplier_id) params.append('supplier_id', filters.supplier_id)
      if (filters.status) params.append('status', filters.status)
      if (filters.payment_status) params.append('payment_status', filters.payment_status)
      if (filters.payment_method) params.append('payment_method', filters.payment_method)
      if (filters.date_from) params.append('date_from', filters.date_from)
      if (filters.date_to) params.append('date_to', filters.date_to)
      if (filters.search) params.append('search', filters.search)
      if (filters.sort) params.append('sort', filters.sort)
      endpoint += `?${params.toString()}`
    }
    const response: any = await this.request(endpoint)
    return response.data || response
  }

  async getPurchase(id: string): Promise<{ purchase: Purchase }> {
    const response: any = await this.request(`/purchases/${id}`)
    return response.data || response
  }

  async createPurchase(purchaseData: PurchaseFormData): Promise<{ purchase: Purchase; message: string }> {
    const response: any = await this.request('/purchases', {
      method: 'POST',
      body: JSON.stringify(purchaseData)
    })
    return response.data || response
  }

  async updatePurchase(id: string, purchaseData: Partial<PurchaseFormData>): Promise<{ purchase: Purchase; message: string }> {
    const response: any = await this.request(`/purchases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(purchaseData)
    })
    return response.data || response
  }

  async deletePurchase(id: string): Promise<{ success: boolean; message: string }> {
    const response: any = await this.request(`/purchases/${id}`, {
      method: 'DELETE'
    })
    return response.data || response
  }

  async getPurchaseStats(filters?: { date_from?: string; date_to?: string }): Promise<PurchaseStats> {
    let endpoint = '/purchases/stats'
    if (filters) {
      const params = new URLSearchParams()
      if (filters.date_from) params.append('date_from', filters.date_from)
      if (filters.date_to) params.append('date_to', filters.date_to)
      if (params.toString()) endpoint += `?${params.toString()}`
    }
    const response: any = await this.request(endpoint)
    return response.data?.stats || response.stats || response
  }

  async searchPurchases(query: string, limit?: number): Promise<{ purchases: Purchase[] }> {
    const params = new URLSearchParams()
    params.append('q', query)
    if (limit) params.append('limit', limit.toString())

    const response: any = await this.request(`/purchases/search?${params.toString()}`)
    return response.data || response
  }

  async receivePurchase(id: string, receivedQuantities?: Record<string, number>): Promise<{ purchase: Purchase; message: string }> {
    const response: any = await this.request(`/purchases/${id}/receive`, {
      method: 'POST',
      body: JSON.stringify({ received_quantities: receivedQuantities })
    })
    return response.data || response
  }

  async recordPurchasePayment(id: string, amount: number, paymentData?: { payment_method?: string; notes?: string }): Promise<{ purchase: Purchase; message: string }> {
    const response: any = await this.request(`/purchases/${id}/payment`, {
      method: 'POST',
      body: JSON.stringify({ amount, ...paymentData })
    })
    return response.data || response
  }

  // Auditor Management
  async inviteAuditor(email: string, message?: string): Promise<{ success: boolean; message: string; data?: any }> {
    return this.request('/auditors/invite', {
      method: 'POST',
      body: JSON.stringify({ email, message })
    })
  }

  async getAuditorAccessList(): Promise<{ success: boolean; data: any[] }> {
    return this.request('/auditors/access-list')
  }

  async revokeAuditorAccess(accessId: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/auditors/revoke-access/${accessId}`, {
      method: 'DELETE'
    })
  }

  async switchBusiness(merchantId: string): Promise<{ success: boolean }> {
    return this.request('/auditors/switch-business', {
      method: 'POST',
      body: JSON.stringify({ merchant_id: merchantId })
    })
  }

  async getInvitationDetails(token: string): Promise<{ success: boolean; data: any }> {
    return this.request(`/auditors/invitation-details?token=${token}`)
  }

  async acceptInvitation(token: string, fullName: string, password: string): Promise<{ success: boolean; data: any }> {
    return this.request('/auditors/accept-invitation', {
      method: 'POST',
      body: JSON.stringify({ token, full_name: fullName, password })
    })
  }

  async acceptInvitationExistingUser(token: string): Promise<{ success: boolean; message: string; data: any }> {
    return this.request('/auditors/accept-existing', {
      method: 'POST',
      body: JSON.stringify({ token })
    })
  }

  async getPendingInvitations(status?: string): Promise<{ success: boolean; data: any[] }> {
    const endpoint = status ? `/auditors/invitations?status=${status}` : '/auditors/invitations'
    return this.request(endpoint)
  }

  async cancelInvitation(invitationId: number): Promise<{ success: boolean; message: string }> {
    return this.request(`/auditors/invitations/${invitationId}`, {
      method: 'DELETE'
    })
  }

  async resendInvitation(invitationId: number): Promise<{ success: boolean; message: string; data: any }> {
    return this.request(`/auditors/invitations/${invitationId}/resend`, {
      method: 'POST'
    })
  }
}

export const apiClient = new ApiClient()
export default apiClient